import { useCallback, useEffect, useRef, useState } from 'react';

type UseTimedPollingProps = {
  fetchFn: () => void;
  timeLimit: number; // in milliseconds
  pollInterval?: number;
};

export const useTimedPolling = ({
  fetchFn,
  timeLimit,
  pollInterval = 1000,
}: UseTimedPollingProps) => {
  const [isPolling, setIsPolling] = useState(false);
  const isMountedRef = useRef(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up function to prevent memory leaks
  const cleanupPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isPolling) {
      // Start polling
      intervalRef.current = setInterval(() => {
        fetchFn();
      }, pollInterval);

      // Stop polling after the time limit
      timeoutRef.current = setTimeout(() => {
        cleanupPolling();
        if (isMountedRef.current) {
          setIsPolling(false);
        }
      }, timeLimit);
    }

    return cleanupPolling;
  }, [isPolling, fetchFn, timeLimit, pollInterval, cleanupPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      cleanupPolling();
    };
  }, [cleanupPolling]);

  const startTimedPolling = useCallback(() => {
    if (isMountedRef.current) {
      // Clean up any existing polling before starting a new one
      cleanupPolling();
      setIsPolling(true);
    }
  }, [cleanupPolling]);

  return {
    isPolling,
    startTimedPolling,
  };
};
